/**
 * @fileoverview Crew Pipeline Check
 * @description Validates a crew assignment as it travels the roster pipeline.
 *
 *              The page has three bands, top to bottom: the leg under test,
 *              the pipeline that will be walked, and the result. Comparing
 *              only the two ends of a pipeline tells you something is wrong;
 *              comparing each hop tells you where — so the pipeline is
 *              editable in place rather than fixed in code.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Divider, Fade, IconButton,
  Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Layers, Play, Plus, RotateCcw, Save, Trash2, Workflow,
} from 'lucide-react';
import { crewPipelineCheckApi } from '../services/crewPipelineCheckApi';
import { useRunProgress } from '../hooks/useRunProgress';
import PipelineProgress from '../components/PipelineProgress';
import FlowEditor from '../components/FlowEditor';
import ComparisonRules from '../components/ComparisonRules';
import CheckpointDialog from '../components/CheckpointDialog';
import ChainResults from '../components/ChainResults';

const TEAL = '#0097A9';
const NAVY = '#01426A';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', bgcolor: '#fff',
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(0,151,169,0.10)' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: TEAL },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: TEAL, borderWidth: 2,
  },
};

const today = () => new Date().toISOString().slice(0, 10);

const CrewPipelineCheck = () => {
  /* Leg under test */
  const [flight, setFlight] = useState('');
  const [flightDate, setFlightDate] = useState(today);
  const [station, setStation] = useState('');
  const [airline, setAirline] = useState('AS');

  /* Flow */
  const [catalog, setCatalog] = useState(null);
  const [flow, setFlow] = useState(null);
  const [savedFlows, setSavedFlows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [formError, setFormError] = useState('');

  /* Checkpoint dialog */
  const [editing, setEditing] = useState(null); // { index, checkpoint, schema }

  const { run, events, error: runError, starting, active, displayPercent, elapsedMs, start, cancel, reset } =
    useRunProgress();

  /* ── Load the catalog and the caller's saved flows ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cat, flows] = await Promise.all([
          crewPipelineCheckApi.getCatalog(),
          crewPipelineCheckApi.listFlows().catch(() => ({ flows: [] })),
        ]);
        if (cancelled) return;
        setCatalog(cat);
        setFlow(cat.defaultFlow);
        setSavedFlows(flows.flows || []);
      } catch (err) {
        if (!cancelled) setFormError(err.message || 'Could not load the checkpoint catalog.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const runParams = useMemo(
    () => ({
      flight: flight.replace(/\D/g, ''),
      date: flightDate,
      station: station.trim().toUpperCase(),
      airline: airline.trim().toUpperCase() || 'AS',
    }),
    [flight, flightDate, station, airline],
  );

  const fieldLabels = useMemo(
    () => Object.fromEntries((catalog?.crewFields || []).map((f) => [f.key, f.label])),
    [catalog],
  );

  const legReady =
    Boolean(runParams.flight) && /^\d{4}-\d{2}-\d{2}$/.test(runParams.date) &&
    /^[A-Z]{3}$/.test(runParams.station);

  const enabledCount = (flow?.checkpoints || []).filter((c) => c.enabled).length;
  const canRun = legReady && enabledCount >= 2 && !active && !starting;

  const updateFlow = useCallback((next) => {
    setFlow(next);
    setDirty(true);
    setNotice('');
  }, []);

  /* ── Checkpoint dialog ── */
  const schemaFor = useCallback(
    (kind) => (catalog?.kinds || []).find((k) => k.kind === kind),
    [catalog],
  );

  const openAdd = useCallback(async (kind) => {
    try {
      const res = await crewPipelineCheckApi.getBlankCheckpoint(kind);
      setEditing({ index: -1, checkpoint: res.checkpoint, schema: schemaFor(kind) });
    } catch (err) {
      setFormError(err.message || 'Could not start a new checkpoint.');
    }
  }, [schemaFor]);

  const openEdit = useCallback((index) => {
    const checkpoint = flow.checkpoints[index];
    setEditing({ index, checkpoint, schema: schemaFor(checkpoint.kind) });
  }, [flow, schemaFor]);

  const commitCheckpoint = useCallback((checkpoint) => {
    setEditing(null);
    updateFlow({
      ...flow,
      checkpoints:
        editing.index < 0
          ? [...flow.checkpoints, checkpoint]
          : flow.checkpoints.map((c, i) => (i === editing.index ? checkpoint : c)),
    });
  }, [editing, flow, updateFlow]);

  /* ── Saved flows ── */
  const loadSaved = useCallback(async (summary) => {
    try {
      const res = await crewPipelineCheckApi.getFlow(summary.id);
      setFlow(res.flow);
      setDirty(false);
      setNotice(`Loaded "${res.flow.name}".`);
      reset();
    } catch (err) {
      setFormError(err.message || 'Could not load that flow.');
    }
  }, [reset]);

  const saveCurrent = useCallback(async () => {
    setSaving(true);
    setFormError('');
    try {
      const res = await crewPipelineCheckApi.saveFlow({
        id: typeof flow.id === 'number' ? flow.id : undefined,
        name: flow.name,
        description: flow.description || '',
        comparison: flow.comparison,
        checkpoints: flow.checkpoints,
      });
      setFlow(res.flow);
      setDirty(false);
      setNotice(`Saved "${res.flow.name}".`);
      const flows = await crewPipelineCheckApi.listFlows();
      setSavedFlows(flows.flows || []);
    } catch (err) {
      setFormError(err.message || 'Could not save the flow.');
    } finally {
      setSaving(false);
    }
  }, [flow]);

  const deleteSaved = useCallback(async (id) => {
    try {
      await crewPipelineCheckApi.deleteFlow(id);
      const flows = await crewPipelineCheckApi.listFlows();
      setSavedFlows(flows.flows || []);
      if (flow?.id === id) {
        setFlow(catalog.defaultFlow);
        setDirty(false);
      }
    } catch (err) {
      setFormError(err.message || 'Could not delete that flow.');
    }
  }, [flow, catalog]);

  const resetToBuiltIn = useCallback(() => {
    setFlow(catalog.defaultFlow);
    setDirty(false);
    setNotice('Reset to the built-in roster chain.');
    reset();
  }, [catalog, reset]);

  /* ── Run ── */
  const handleRun = useCallback(() => {
    setFormError('');
    setNotice('');
    // The flow is sent inline so unsaved edits run exactly as they appear
    // on screen; the backend fills in any credential the editor never saw.
    start({ ...runParams, flow: { ...flow, id: typeof flow.id === 'number' ? flow.id : undefined } });
  }, [runParams, flow, start]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && canRun) handleRun();
  };

  if (!catalog || !flow) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        {formError ? (
          <Alert severity="error" sx={{ borderRadius: '12px' }}>{formError}</Alert>
        ) : (
          <CircularProgress sx={{ color: TEAL }} />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Fade in timeout={400}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5, mb: 3, borderRadius: '16px',
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
            color: '#fff',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Workflow size={26} />
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                Crew Pipeline Check
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Walk a crew assignment hop by hop and find the stop where it stops matching.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Fade>

      {/* Leg under test */}
      <Fade in timeout={550}>
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
          <Typography variant="subtitle2" fontWeight={800} color={NAVY} mb={0.5}>
            Which leg?
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            The departure station is required — the upstream roster systems key crew on it.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }}>
            <TextField
              size="small" label="Flight number" placeholder="2444" sx={{ ...inputSx, flex: 1 }}
              value={flight} onChange={(e) => setFlight(e.target.value)} onKeyDown={handleKeyDown}
              inputProps={{ inputMode: 'numeric', 'aria-label': 'Flight number' }}
              helperText="Digits only"
            />
            <TextField
              size="small" type="date" label="Flight date" sx={{ ...inputSx, flex: 1 }}
              value={flightDate} onChange={(e) => setFlightDate(e.target.value)}
              InputLabelProps={{ shrink: true }} helperText="Departure date of the leg"
            />
            <TextField
              size="small" label="Station" placeholder="SEA" sx={{ ...inputSx, flex: 0.7 }}
              value={station} onChange={(e) => setStation(e.target.value.toUpperCase())}
              onKeyDown={handleKeyDown}
              inputProps={{ maxLength: 3, 'aria-label': 'Departure station' }}
              helperText="3-letter IATA code"
            />
            <TextField
              size="small" label="Airline" sx={{ ...inputSx, flex: 0.5 }}
              value={airline} onChange={(e) => setAirline(e.target.value.toUpperCase())}
              inputProps={{ maxLength: 3 }} helperText="IATA code"
            />
            <Button
              variant="contained"
              startIcon={starting ? <CircularProgress size={15} color="inherit" /> : <Play size={16} />}
              onClick={handleRun}
              disabled={!canRun}
              sx={{
                bgcolor: TEAL, borderRadius: '10px', textTransform: 'none', fontWeight: 700,
                px: 3, py: 1, whiteSpace: 'nowrap', mt: { md: 0.25 },
                '&:hover': { bgcolor: '#007f8f' },
              }}
            >
              {active ? 'Running…' : 'Run check'}
            </Button>
          </Stack>
          {legReady && enabledCount < 2 && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: '10px' }}>
              Enable at least two checkpoints below — a single checkpoint has nothing to compare against.
            </Alert>
          )}
        </Paper>
      </Fade>

      {/* Progress */}
      {run && (
        <PipelineProgress
          run={run}
          events={events}
          percent={displayPercent}
          elapsedMs={elapsedMs}
          active={active}
          onCancel={cancel}
        />
      )}

      {(formError || runError) && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setFormError('')}>
          {formError || runError}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setNotice('')}>
          {notice}
        </Alert>
      )}

      {/* Result */}
      {run?.result && (
        <Fade in timeout={400}>
          <Box mb={3}>
            <ChainResults result={run.result} fieldLabels={fieldLabels} />
          </Box>
        </Fade>
      )}

      {/* Flow */}
      <Fade in timeout={700}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            justifyContent="space-between" alignItems={{ lg: 'center' }} spacing={2} mb={2.5}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: 40, height: 40, borderRadius: '10px', bgcolor: '#0f172a', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Layers size={20} color="#fff" />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1, maxWidth: 460 }}>
                <TextField
                  size="small" variant="standard" fullWidth
                  value={flow.name}
                  onChange={(e) => updateFlow({ ...flow, name: e.target.value })}
                  inputProps={{ 'aria-label': 'Flow name' }}
                  InputProps={{ sx: { fontWeight: 800, color: NAVY, fontSize: '1rem' } }}
                />
                <Stack direction="row" spacing={0.75} alignItems="center" mt={0.5} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={dirty ? 'Unsaved changes' : flow.builtin ? 'Built-in' : 'Saved'}
                    sx={{
                      height: 18, fontSize: '0.62rem', fontWeight: 800,
                      bgcolor: dirty ? '#fffbeb' : '#f1f5f9',
                      color: dirty ? '#b45309' : '#64748b',
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Shared with everyone who can open this page
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            <Stack
              direction="row" spacing={1} flexShrink={0} flexWrap="wrap" useFlexGap
              alignItems="center"
            >
              <Autocomplete
                size="small" sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { height: 40 } }}
                options={savedFlows}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={null} blurOnSelect
                onChange={(_, value) => { if (value) loadSaved(value); }}
                noOptionsText="No saved flows yet"
                renderOption={(props, option) => (
                  <Box
                    component="li" {...props} key={option.id}
                    sx={{ display: 'flex !important', justifyContent: 'space-between', gap: 1 }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {option.checkpointCount} checkpoints
                        {option.updatedBy ? ` · last edited by ${option.updatedBy}` : ''}
                      </Typography>
                    </Box>
                    <Tooltip title="Delete saved flow">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); deleteSaved(option.id); }}
                        sx={{ color: '#dc2626' }}
                      >
                        <Trash2 size={14} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                renderInput={(params) => <TextField {...params} label="Open a saved flow" sx={inputSx} />}
              />
              <Tooltip title="Discard changes and reload the built-in roster chain">
                <span>
                  <Button
                    variant="outlined" startIcon={<RotateCcw size={15} />}
                    onClick={resetToBuiltIn}
                    sx={{
                      height: 40, borderColor: '#cbd5e1', color: '#475569', borderRadius: '10px',
                      textTransform: 'none', fontWeight: 600, px: 2, whiteSpace: 'nowrap',
                      '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
                    }}
                  >
                    Reset
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={dirty ? 'Save this pipeline for everyone' : 'No changes to save'}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={15} />}
                    onClick={saveCurrent} disabled={saving || !dirty || !flow.name?.trim()}
                    sx={{
                      height: 40, bgcolor: NAVY, borderRadius: '10px', textTransform: 'none',
                      fontWeight: 700, px: 2.5, whiteSpace: 'nowrap',
                      '&:hover': { bgcolor: '#012c47' },
                      '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
                    }}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <FlowEditor
            flow={flow}
            kinds={catalog.kinds}
            onChange={updateFlow}
            onEdit={openEdit}
            onAdd={openAdd}
          />

          <Divider sx={{ my: 3 }} />

          <ComparisonRules
            rules={flow.comparison?.rules || catalog.defaultRules}
            crewFields={catalog.crewFields}
            modes={catalog.comparisonModes}
            defaultRules={catalog.defaultRules}
            onChange={(rules) => updateFlow({ ...flow, comparison: { ...flow.comparison, rules } })}
          />
        </Paper>
      </Fade>

      {editing && (
        <CheckpointDialog
          open
          onClose={() => setEditing(null)}
          onSave={commitCheckpoint}
          checkpoint={editing.checkpoint}
          schema={editing.schema}
          crewFields={catalog.crewFields}
          tokens={catalog.tokens}
          runParams={runParams}
          flowId={typeof flow.id === 'number' ? flow.id : undefined}
        />
      )}
    </Box>
  );
};

export default CrewPipelineCheck;
